import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Alert,
  AuthShell,
  Box,
  Button,
  CardSkeleton,
  Link,
  Stack,
  Typography,
  useToast,
} from '@platform/design-system';
import { RouterLink, useApi, useSession } from '@platform/app-kit';
import { waitUntilReadable } from '../after-sign-in.ts';
import { READY_STATUSES, isSlug, useSignupStatus } from '../api/queries.ts';
import { AUTH_FRAME } from '../shell/brand.ts';
import { useDocumentTitle } from '../shell/document-title.ts';

/** Two minutes past payment before the page says it is taking longer than usual. */
const SLOW_AFTER_MS = 2 * 60_000;
/** After `active`, how many times to fetch new tokens before asking for a fresh sign-in. */
const REFRESH_ATTEMPTS = 5;
const REFRESH_EVERY_MS = 2_000;

/**
 * `/signup/complete?slug=<p-…>[&session_id=]` — the wait page of a personal sign-up. It polls
 * `GET /signup/status` (public) and:
 * - before payment, waits for the payment page and sends the browser there;
 * - back from the payment page (`session_id`), waits for the payment to land and the account to
 *   be set up — saying so plainly, and that we email when it is slow;
 * - once `active`, force-refreshes the session (identity rewrote the person's claims: the new ID
 *   token carries their one membership), runs `GET /me`, and opens the app. A session that did
 *   not survive the round trip (tokens in memory) is asked to sign in instead.
 * Customer copy only: no reason text, no technical detail (#24).
 */
export function SignupCompletePage() {
  const [params] = useSearchParams();
  const slug = params.get('slug');
  const returned = params.has('session_id');
  const session = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const status = useSignupStatus(slug);
  const api = useApi();
  const [slow, setSlow] = useState(false);
  const [stuck, setStuck] = useState(false);
  const redirected = useRef(false);
  useDocumentTitle('Setting up your account');

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  const data = status.data;
  const state = data?.status;
  const checkoutUrl = data?.checkoutUrl;
  const ready = state !== undefined && READY_STATUSES.includes(state);

  // Pay-first: the status carries the payment page once billing has created it.
  useEffect(() => {
    if (!checkoutUrl || returned || redirected.current) return;
    redirected.current = true;
    const target = new URL(checkoutUrl, window.location.origin);
    if (target.origin === window.location.origin)
      void navigate(`${target.pathname}${target.search}`, { replace: true });
    else window.location.assign(target.toString());
  }, [checkoutUrl, returned, navigate]);

  // Active: new tokens carry the membership; then the app opens.
  const { status: sessionStatus, refresh } = session;
  useEffect(() => {
    if (!ready || sessionStatus !== 'authenticated') return;
    let cancelled = false;
    void (async () => {
      for (let attempt = 0; attempt < REFRESH_ATTEMPTS && !cancelled; attempt++) {
        try {
          const principal = await refresh();
          if (cancelled || !principal) return;
          if (principal.tenantId) {
            // The new tokens carry the membership, but the API lets the tenant in a little later:
            // ask quietly until it answers, then open the app (no refused calls on its first screen).
            await waitUntilReadable(() => api.identity.Self_get(), { cancelled: () => cancelled });
            if (cancelled) return;
            toast.success('Your account is ready');
            void navigate('/', { replace: true });
            return;
          }
        } catch {
          // A failed refresh is retried; the person is told only if every attempt fails.
        }
        await new Promise((resolve) => setTimeout(resolve, REFRESH_EVERY_MS));
      }
      if (!cancelled) setStuck(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, sessionStatus, refresh, navigate, toast, api]);

  const notFound = (status.error as { status?: unknown } | null)?.status === 404;
  const issue = state === 'failed' ? (data?.issue ?? 'delayed') : undefined;
  const signedIn = sessionStatus === 'authenticated';
  const loginHref = `/login?returnTo=${encodeURIComponent('/')}`;

  let title: string;
  let body: ReactNode;
  if (!isSlug(slug)) {
    title = 'Nothing to show here';
    body = (
      <Alert severity="warning">
        This page needs the link from your sign-up.{' '}
        <Link component={RouterLink} href={signedIn ? '/subscribe' : '/signup'}>
          {signedIn ? 'Choose your plan' : 'Create an account'}
        </Link>
      </Alert>
    );
  } else if (notFound) {
    title = 'We couldn’t find that sign-up';
    body = (
      <Stack spacing={2}>
        <Typography variant="body1">
          The link may be old. Sign in to pick up where you left off.
        </Typography>
        <Box>
          <Button
            component={RouterLink}
            href={signedIn ? '/subscribe' : loginHref}
            variant="contained"
          >
            {signedIn ? 'Choose your plan' : 'Sign in'}
          </Button>
        </Box>
      </Stack>
    );
  } else if (ready) {
    title = 'Your account is ready';
    body = signedIn ? (
      stuck ? (
        <Stack spacing={2}>
          <Alert severity="info" role="status">
            Your plan is active. Sign in again to open your account.
          </Alert>
          <Box>
            <Button
              variant="contained"
              onClick={() =>
                void session.signOut().then(() => navigate(loginHref, { replace: true }))
              }
            >
              Sign in again
            </Button>
          </Box>
        </Stack>
      ) : (
        <Typography variant="body1" role="status">
          Opening your account…
        </Typography>
      )
    ) : (
      <Stack spacing={2}>
        <Alert severity="success" role="status">
          Payment received and your account is set up. Sign in to open it.
        </Alert>
        <Box>
          <Button component={RouterLink} href={loginHref} variant="contained" size="large">
            Sign in
          </Button>
        </Box>
      </Stack>
    );
  } else if (issue === 'checkoutExpired') {
    title = 'Your payment page timed out';
    body = (
      <Stack spacing={2}>
        <Alert severity="warning" role="status">
          Nothing was charged. Start again when you’re ready.
        </Alert>
        <Box>
          <Button component={RouterLink} href="/subscribe" variant="contained">
            Choose your plan
          </Button>
        </Box>
      </Stack>
    );
  } else if (issue === 'delayed') {
    title = 'We’re finishing your account';
    body = (
      <Alert severity="info" role="status">
        Setting up your account is taking longer than expected. Your payment is safe and there’s
        nothing you need to do. We’ll email you as soon as it’s ready.
      </Alert>
    );
  } else if (state === 'offboarding' || state === 'deprovisioned') {
    title = 'This account is closed';
    body = (
      <Typography variant="body1">
        This sign-up can’t continue.{' '}
        <Link component={RouterLink} href={signedIn ? '/subscribe' : loginHref}>
          Start again
        </Link>
      </Typography>
    );
  } else if (state === 'pendingPayment' && !returned) {
    title = checkoutUrl ? 'Taking you to the payment page' : 'Preparing your payment page';
    body = (
      <Stack spacing={2}>
        <Typography variant="body1" role="status">
          {checkoutUrl
            ? 'One moment…'
            : 'This takes a few seconds. You’ll go straight to the payment page.'}
        </Typography>
        {checkoutUrl ? (
          <Typography variant="body2" color="text.secondary">
            Nothing happening?{' '}
            <Link href={checkoutUrl} variant="body2">
              Open the payment page
            </Link>
          </Typography>
        ) : (
          <CardSkeleton lines={2} title={false} />
        )}
      </Stack>
    );
  } else if (state === 'pendingPayment' || state === 'provisioning') {
    title = state === 'provisioning' ? 'Setting up your account' : 'Confirming your payment';
    body = (
      <Stack spacing={2}>
        <Typography variant="body1" role="status">
          {slow
            ? 'This is taking a little longer than usual. Your account will be ready shortly — you can close this page, and we’ll email you when it is.'
            : state === 'provisioning'
              ? 'Payment received. Your account will be ready in a moment.'
              : 'Thanks! This usually takes a few seconds.'}
        </Typography>
        <CardSkeleton lines={2} title={false} />
      </Stack>
    );
  } else if (status.isError) {
    title = 'Checking on your account';
    body = (
      <Alert severity="info" role="status">
        We couldn’t check on your account just now. We’ll keep trying — you can also close this
        page, and we’ll email you when your account is ready.
      </Alert>
    );
  } else {
    title = 'Checking on your account';
    body = <CardSkeleton lines={2} title={false} />;
  }

  return (
    <AuthShell
      brandName={AUTH_FRAME.brandName}
      pitch="Almost there."
      supporting="Your account opens as soon as the payment goes through."
      title={title}
    >
      {body}
    </AuthShell>
  );
}
